/**
 * Informes.
 *
 * Todo sale de agregaciones hechas en la base de datos, así que se abre rápido
 * aunque lleves años de movimientos. Incluye la **proyección de flujo** («¿me
 * alcanza hasta fin de mes?») y la **auditoría del gasto hormiga**, que compara lo
 * que se te va en menudencias con algo tangible que tú eliges.
 */

import { useQuery } from '@tanstack/react-query';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { useState } from 'react';

import { formatCOP, formatMonthLabel } from '@eldescuadre/core';

import {
  getCategoryBreakdown,
  getHormigaAudit,
  getProjection,
  getSeries,
  getSummary,
} from '../lib/endpoints';
import { usePeriod, monthOf } from '../lib/period';
import { SavingsStreak } from '../components/SavingsStreak';
import { BalanceTrajectory } from '../components/BalanceCharts';
import { BackupButton, ExportButton } from '../components/ExportReports';
import { BarChart, DonutChart, LineChart } from '../components/charts';
import { Card, EmptyState, ErrorNote, Field, Loading, Money, Note, Progress, Stat } from '../components/ui';

export function ReportsPage(): React.JSX.Element {
  const [comparison, setComparison] = useState('meses de arriendo');
  const [comparisonValue, setComparisonValue] = useState(950_000);

  /*
   * El período lo manda la cabecera, y vale para todas las pantallas.
   *
   * Cuando se mira por meses se sigue mandando el mes, porque los informes por mes
   * (la serie de 12 meses, los presupuestos) necesitan saber de qué mes se habla.
   * Cuando se mira por día, año o a mano, se manda el rango de fechas: el servidor
   * lo respeta y compara contra el tramo anterior de la misma duración.
   */
  const period = usePeriod((s) => s.period);
  const range = usePeriod((s) => s.range);

  const monthKey = period.kind === 'month' ? period.monthKey : monthOf(range.to);
  const reportRange = period.kind === 'month' ? undefined : { from: range.from, to: range.to };

  const summary = useQuery({
    queryKey: ['reports', 'summary', monthKey, range.from, range.to],
    queryFn: () => getSummary(monthKey, reportRange),
  });
  const categories = useQuery({
    queryKey: ['reports', 'categories', monthKey, range.from, range.to],
    queryFn: () => getCategoryBreakdown(monthKey, reportRange),
  });
  const series = useQuery({
    queryKey: ['reports', 'series', 12, monthKey],
    queryFn: () => getSeries(12, monthKey),
  });
  const projection = useQuery({
    queryKey: ['reports', 'projection', 6, monthKey],
    queryFn: () => getProjection(6, monthKey),
  });
  const hormiga = useQuery({
    queryKey: ['reports', 'hormiga', monthKey, comparison, comparisonValue],
    queryFn: () => getHormigaAudit(monthKey, comparisonValue, comparison),
  });

  const data = summary.data;

  return (
    <div className="space-y-4 animate-ed-fade-in">
      <header>
        <h1 className="font-display text-2xl font-extrabold tracking-tight">Informes</h1>
        <p className="mt-0.5 text-sm capitalize text-ed-text-muted">
          Tus cifras, sin adornos · {range.label}
        </p>
      </header>

      {/* El período se cambia en la cabecera y vale para todas las pantallas. Con la
          dona, las barras y la línea se ve de un vistazo lo que antes había que leer
          fila por fila. */}

      {summary.isLoading && <Loading label="Calculando…" />}
      {summary.isError && <ErrorNote message="No se pudieron cargar los informes." />}

      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat
              label="Gastaste"
              value={<Money amount={-data.monthExpense} signed />}
              hint={
                data.expenseChangePercent !== null ? (
                  <span
                    className={
                      data.expenseChangePercent > 0 ? 'text-ed-coral-light' : 'text-ed-success'
                    }
                  >
                    {data.expenseChangePercent > 0 ? (
                      <TrendingUp size={11} className="mr-0.5 inline" aria-hidden="true" />
                    ) : (
                      <TrendingDown size={11} className="mr-0.5 inline" aria-hidden="true" />
                    )}
                    {Math.abs(Math.round(data.expenseChangePercent))} % frente al mes pasado
                  </span>
                ) : (
                  'Sin comparación'
                )
              }
            />
            <Stat
              label="Entró"
              value={<Money amount={data.monthIncome} />}
              tone="positive"
            />
            <Stat
              label="Flujo del mes"
              value={<Money amount={data.cashFlow} signed />}
              hint="Lo que entró menos lo que salió"
              tone={data.cashFlow >= 0 ? 'positive' : 'negative'}
            />
          </div>

          {/* Patrimonio */}
          <Card title="Tu situación">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-ed-text-muted">Tienes (cuentas de activo)</dt>
                <dd><Money amount={data.assets} className="font-semibold" /></dd>
              </div>
              {data.creditCardDebt > 0 && (
                <div className="flex justify-between">
                  <dt className="text-ed-text-muted">Tarjetas de crédito</dt>
                  <dd><Money amount={data.creditCardDebt} className="font-semibold text-ed-coral-light" /></dd>
                </div>
              )}
              {data.debtsTotal > 0 && (
                <div className="flex justify-between">
                  <dt className="text-ed-text-muted">Créditos</dt>
                  <dd><Money amount={data.debtsTotal} className="font-semibold text-ed-coral-light" /></dd>
                </div>
              )}
              {data.fiadoTotal > 0 && (
                <div className="flex justify-between">
                  <dt className="text-ed-text-muted">Fiado en tiendas</dt>
                  <dd><Money amount={data.fiadoTotal} className="font-semibold text-ed-coral-light" /></dd>
                </div>
              )}
              {data.reservedForGoals > 0 && (
                <div className="flex justify-between">
                  <dt className="text-ed-text-muted">Apartado para metas</dt>
                  <dd><Money amount={data.reservedForGoals} className="font-semibold text-ed-gold" /></dd>
                </div>
              )}
              <div className="flex justify-between border-t border-ed-bg-border/30 pt-2">
                <dt className="font-medium">Patrimonio neto</dt>
                <dd>
                  <Money
                    amount={data.netWorth}
                    className={`font-display text-lg font-bold ${
                      data.netWorth >= 0 ? 'text-ed-success' : 'text-ed-coral-light'
                    }`}
                  />
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-xs text-ed-text-muted">
              El patrimonio es lo que tienes menos lo que debes. Puede ser negativo y no pasa nada:
              lo importante es que vaya subiendo.
            </p>
          </Card>

          {/* La racha, antes de los desgloses: es lo primero que se quiere saber. */}
          <SavingsStreak />

          {/* Cómo ha llegado el saldo hasta hoy. Lo tenía Cuentify y faltaba. */}
          <BalanceTrajectory monthKey={monthKey} />

          {/* Desglose por categoría. La dona va arriba y la lista detallada debajo:
              la dona da la forma de un vistazo, la lista da las cifras exactas y los
              cambios frente al mes pasado. */}
          <Card title="En qué se fue">
            {categories.data && categories.data.categories.length === 0 && (
              <EmptyState
                title="Sin gastos este mes"
                message="Cuando registres gastos, aquí verás el reparto por categorías."
              />
            )}

            {categories.data && categories.data.categories.length > 0 && (
              <>
                <DonutChart
                  data={categories.data.categories.map((row) => ({
                    label: row.name,
                    value: row.total,
                    color: row.color ?? undefined,
                  }))}
                  centerLabel="Gastado"
                />

                <ul className="mt-5 space-y-3 border-t border-ed-bg-border/30 pt-4">
                  {categories.data.categories.map((row) => (
                    <li key={row.categoryId ?? row.name}>
                      <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
                        <span className="flex items-center gap-2 truncate">
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: row.color }}
                            aria-hidden="true"
                          />
                          {row.name}
                        </span>
                        <span className="flex shrink-0 items-baseline gap-2">
                          <Money amount={-row.total} signed className="font-semibold" />
                          <span className="text-xs text-ed-text-muted">
                            {Math.round(row.percentOfTotal)} %
                          </span>
                        </span>
                      </div>
                      <Progress
                        percent={row.percentOfTotal}
                        label={`${row.name}: ${Math.round(row.percentOfTotal)} por ciento`}
                      />
                      {row.changePercent !== null && Math.abs(row.changePercent) >= 15 && (
                        <p
                          className={`mt-1 text-xs ${
                            row.changePercent > 0 ? 'text-ed-coral-light' : 'text-ed-success'
                          }`}
                        >
                          {row.changePercent > 0 ? 'Subió' : 'Bajó'}{' '}
                          {Math.abs(Math.round(row.changePercent))} % ({formatCOP(row.previousTotal)}{' '}
                          el mes pasado)
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>

          {/* Serie de 12 meses: la tendencia, en línea y en barras. */}
          {series.data && series.data.series.some((p) => p.expense > 0 || p.income > 0) && (
            <Card title="Últimos 12 meses">
              <LineChart
                data={series.data.series.map((point) => ({
                  label: formatMonthLabel(point.monthKey).split(' ')[0]?.slice(0, 3) ?? point.monthKey,
                  value: point.expense,
                }))}
                label="Gastos por mes"
                emptyMessage="Todavía no hay suficientes meses para ver la tendencia."
              />

              <div className="mt-6 border-t border-ed-bg-border/30 pt-4">
                <BarChart
                  data={series.data.series.map((point) => ({
                    label: formatMonthLabel(point.monthKey).split(' ')[0]?.slice(0, 3) ?? point.monthKey,
                    value: point.income,
                    second: point.expense,
                  }))}
                  firstLabel="Ingresos"
                  secondLabel="Gastos"
                />
              </div>
            </Card>
          )}

          {/* La comparación mes a mes, en barras y en su propia tarjeta. */}
          {summary.data?.previous && (
            <Card title="Este mes contra el pasado">
              <BarChart
                data={[
                  {
                    label: formatMonthLabel(summary.data.previous.monthKey).split(' ')[0]?.slice(0, 3) ?? 'Anterior',
                    value: summary.data.previous.income,
                    second: summary.data.previous.expense,
                  },
                  {
                    label: 'Este mes',
                    value: summary.data.monthIncome,
                    second: summary.data.monthExpense,
                  },
                ]}
                firstLabel="Ingresos"
                secondLabel="Gastos"
              />
              <p className="mt-3 text-xs text-ed-text-muted">
                {summary.data.monthExpense > summary.data.previous.expense
                  ? `Estás gastando ${formatCOP(summary.data.monthExpense - summary.data.previous.expense)} más que el mes pasado.`
                  : summary.data.monthExpense < summary.data.previous.expense
                    ? `Estás gastando ${formatCOP(summary.data.previous.expense - summary.data.monthExpense)} menos que el mes pasado.`
                    : 'Estás gastando exactamente lo mismo que el mes pasado.'}
              </p>
            </Card>
          )}


          {/* Gasto hormiga */}
          {hormiga.data && hormiga.data.hormigaSpent > 0 && (
            <Card title="El gasto hormiga">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3">
                  <p className="text-xs uppercase tracking-wide text-ed-text-muted">Este mes</p>
                  <Money amount={hormiga.data.hormigaSpent} className="font-display text-lg font-bold" />
                </div>
                <div className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3">
                  <p className="text-xs uppercase tracking-wide text-ed-text-muted">Al año</p>
                  <Money amount={hormiga.data.annualProjection} className="font-display text-lg font-bold text-ed-coral-light" />
                </div>
                <div className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3">
                  <p className="text-xs uppercase tracking-wide text-ed-text-muted">Al día</p>
                  <Money amount={hormiga.data.dailyAverage} className="font-display text-lg font-bold" />
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <Field label="Compáralo con" htmlFor="comparacion">
                  <input
                    id="comparacion"
                    className="ed-input"
                    value={comparison}
                    onChange={(e) => setComparison(e.target.value)}
                    placeholder="meses de arriendo"
                    autoComplete="off"
                  />
                </Field>
                <Field label="Que vale (pesos)" htmlFor="valor-comparacion">
                  <input
                    id="valor-comparacion"
                    className="ed-input ed-money"
                    inputMode="numeric"
                    value={String(comparisonValue)}
                    onChange={(e) => setComparisonValue(Number(e.target.value.replace(/\D/g, '')) || 0)}
                    autoComplete="off"
                  />
                </Field>
              </div>

              {hormiga.data.comparison && hormiga.data.comparison.units > 0 && (
                <p className="mt-3 rounded-ed border border-ed-gold/30 bg-ed-gold/10 px-3 py-2 text-sm text-ed-gold">
                  Lo que se te va en menudencias equivale a{' '}
                  <strong>{hormiga.data.comparison.units}</strong> {hormiga.data.comparison.label}{' '}
                  al año.
                </p>
              )}

              {hormiga.data.daysOverBudget > 0 && (
                <p className="mt-2 text-xs text-ed-text-muted">
                  Te pasaste de tu caja chica diaria ({formatCOP(hormiga.data.dailyBudget)}) en{' '}
                  {hormiga.data.daysOverBudget}{' '}
                  {hormiga.data.daysOverBudget === 1 ? 'día' : 'días'} del mes.
                </p>
              )}

              <p className="mt-3 text-xs text-ed-text-muted">
                El valor de comparación lo pones tú. Antes venía escrito en el código, y el arriendo
                de cada persona es suyo.
              </p>
            </Card>
          )}

          {/* Proyección de flujo */}
          {projection.data && (
            <Card title="Qué viene">
              <p className="mb-3 text-sm text-ed-text-muted">
                Si nada cambia, tu saldo disponible iría así:
              </p>
              <ul className="space-y-2">
                {projection.data.months.map((month) => (
                  <li key={month.monthKey} className="flex items-center justify-between gap-3 text-sm">
                    <span className="capitalize text-ed-text-muted">
                      {formatMonthLabel(month.monthKey)}
                    </span>
                    <Money
                      amount={month.projectedBalance}
                      className={`font-semibold ${
                        month.projectedBalance < 0 ? 'text-ed-coral-light' : 'text-ed-text-white'
                      }`}
                    />
                  </li>
                ))}
              </ul>

              {projection.data.firstNegativeMonth ? (
                <ErrorNote
                  message={`A este paso, en ${formatMonthLabel(projection.data.firstNegativeMonth)} te quedarías sin dinero.`}
                  hint="Puedes añadir un ingreso, recortar un gasto fijo o revisar tu gasto variable en la pantalla de presupuestos."
                />
              ) : (
                <Note>Con tus cifras actuales, el saldo se mantiene positivo todo el período.</Note>
              )}

              <p className="mt-3 text-xs text-ed-text-muted">{projection.data.disclaimer}</p>
            </Card>
          )}

          {/* Exportaciones */}
          <Card title="Llevarte tus datos">
            <div className="grid gap-2 sm:grid-cols-2">
              <ExportButton
                report={{
                  entity: 'movimientos',
                  label: 'CSV del mes',
                  detail: 'Las 13 columnas de siempre, listo para el contador',
                }}
              />
              <BackupButton />
            </div>

            <p className="mt-3 text-xs text-ed-text-muted">
              El CSV trae las <strong>13 columnas</strong> de siempre, listo para el contador. El
              respaldo en JSON lleva todo tu historial y <strong>nada de seguridad</strong>: ni
              contraseñas, ni sesiones.
            </p>

            <p className="mt-2 text-xs text-ed-text-muted">
              Para los demás informes —deudas, fiado, gastos fijos, cuentas…— están todos en{' '}
              <strong>Ajustes → Llevarte tus datos</strong>.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}
